import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Chip,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description';
import BlockIcon from '@mui/icons-material/Block';
import { SCUIButton } from '../../../../atomic/atoms/SCUIButton/SCUIButton';

const DetailRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
    icon,
    label,
    value,
}) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
        <Box sx={{ color: '#64748B', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
            <Typography
                variant="caption"
                sx={{ color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
                {label}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
                {typeof value === 'string' ? (
                    <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                        {value}
                    </Typography>
                ) : (
                    value
                )}
            </Box>
        </Box>
    </Box>
);

interface RequestDetailModalProps {
    open: boolean;
    onClose: () => void;
    request: {
        requestedRoleName: string;
        requestDate: string;
        status: number;
        statusText: string;
        assignedApproverUsername?: string;
        approvalDate?: string;
        requestNote?: string;
        rejectionReason?: string;
    } | null;
    formatDate: (dateString: string) => string;
}

export const RequestDetailModal: React.FC<RequestDetailModalProps> = ({ open, onClose, request, formatDate }) => {
    const { t } = useTranslation();

    if (!request) return null;

    const getStatusChip = () => {
        const statusMap: Record<number, { color: string; bgcolor: string; icon: React.ReactNode }> = {
            0: { color: '#1565C0', bgcolor: '#E3F2FD', icon: <AccessTimeIcon sx={{ fontSize: 16 }} /> },
            1: { color: '#2E7D32', bgcolor: '#E8F5E9', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
            2: { color: '#C62828', bgcolor: '#FFEBEE', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
        };
        const s = statusMap[request.status] || statusMap[0];
        return (
            <Chip
                icon={s.icon as React.ReactElement}
                label={t(`roleApproval.status.${request.statusText.toLowerCase()}`)}
                size="small"
                sx={{
                    bgcolor: s.bgcolor,
                    color: s.color,
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'inherit' },
                }}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #E2E8F0',
                    px: 3,
                    py: 2.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <InfoOutlinedIcon sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                            {t('roleApproval.myRoleRequests.requestDetail')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {request.requestedRoleName}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#64748B' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 3 }}>
                <DetailRow
                    icon={<SecurityIcon sx={{ fontSize: 20 }} />}
                    label={t('roleApproval.requestedRole')}
                    value={request.requestedRoleName}
                />
                <Divider sx={{ my: 0.5 }} />
                <DetailRow
                    icon={<AccessTimeIcon sx={{ fontSize: 20 }} />}
                    label={t('roleApproval.myRoleRequests.status')}
                    value={getStatusChip()}
                />
                <Divider sx={{ my: 0.5 }} />
                <DetailRow
                    icon={<CalendarTodayIcon sx={{ fontSize: 20 }} />}
                    label={t('roleApproval.myRoleRequests.requestDate')}
                    value={formatDate(request.requestDate)}
                />
                {request.approvalDate && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        <DetailRow
                            icon={<CalendarTodayIcon sx={{ fontSize: 20 }} />}
                            label={t('roleApproval.myRoleRequests.processDate')}
                            value={formatDate(request.approvalDate)}
                        />
                    </>
                )}
                {request.assignedApproverUsername && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        <DetailRow
                            icon={<PersonIcon sx={{ fontSize: 20 }} />}
                            label={t('roleApproval.myRoleRequests.approver')}
                            value={request.assignedApproverUsername}
                        />
                    </>
                )}
                {request.requestNote && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        <DetailRow
                            icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
                            label={t('roleApproval.myRoleRequests.requestNote')}
                            value={
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: '#F8FAFC',
                                        borderRadius: 1.5,
                                        border: '1px solid #E2E8F0',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#334155',
                                            lineHeight: 1.7,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {request.requestNote}
                                    </Typography>
                                </Box>
                            }
                        />
                    </>
                )}

                {request.status === 2 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Box
                            sx={{
                                mt: 1,
                                p: 2.5,
                                backgroundColor: '#FFF5F5',
                                borderRadius: 2,
                                border: '1px solid #FFCDD2',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <BlockIcon sx={{ fontSize: 20, color: '#C62828' }} />
                                <Typography variant="subtitle2" sx={{ color: '#C62828', fontWeight: 600 }}>
                                    {t('roleApproval.rejection.rejectionReasonTitle')}
                                </Typography>
                            </Box>
                            {request.rejectionReason ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#334155',
                                        lineHeight: 1.7,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {request.rejectionReason}
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
                                    {t('roleApproval.rejection.noRejectionReason')}
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: '1px solid #E2E8F0',
                    px: 3,
                    py: 2,
                }}
            >
                <SCUIButton
                    onClick={onClose}
                    type="secondary"
                    buttonType="button"
                    sizeType="fixed"
                    size="m"
                    text={t('general.close')}
                />
            </DialogActions>
        </Dialog>
    );
};
